import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Link,
  Divider,
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { Circle } from '@mui/icons-material';
import ELF_Icon from '../../assets/ELF_Icon+Type_CMYK.png';
import Image from 'mui-image';

interface ButtonLinkProps {
  href: string;
  label: string;
}

const ButtonLink: React.FC<ButtonLinkProps> = ({ href, label }) => (
  <Button
    size="small"
    variant="text"
    color="primary"
    href={href}
    target="_blank"
    rel="noopener"
    sx={{
      textTransform: 'none',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'rgba(210, 215, 85, 0.08)',
      },
      color: '#008578',
    }}
  >
    {label}
  </Button>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`elf-tabpanel-${index}`}
    aria-labelledby={`elf-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const ELFLanding: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Hero Section with ELF Logo */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #D2D755 0%, #008578 100%)',
          color: 'white',
          borderRadius: 3,
          p: 3,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Image
            src={ELF_Icon}
            alt="ELF Logo"
            sx={{ maxWidth: '100px' }}
            width={'40vw'}
          />
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 'bold', mb: 1.5, color: '#fff' }}
        >
          ELF (Educational Loan Fund)
        </Typography>
        <Typography
          variant="body1"
          sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', color: '#fff' }}
        >
          The P.E.O. Educational Loan Fund makes loans available to qualified
          women who desire a higher education and are in need of financial
          assistance. All applicants are recommended by a local P.E.O. chapter.
          At just 2%, the ELF loan offers a competitive rate and creates a
          pathway for education that doesn't lead to insurmountable debt.
        </Typography>
      </Paper>

      {/* Tab Navigation */}
      <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="ELF page tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                minHeight: 64,
              },
            }}
          >
            <Tab label="Information & Resources" />
          </Tabs>
        </Box>

        {/* Information & Resources Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Local Chapter Recommendation Process */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Local Chapter Recommendation Process"
              subheader="Use the resources below to help you identify, interview and recommend your chapter ELF candidate. Recommendations can be submitted year round."
              sx={{
                background: 'linear-gradient(135deg, #D2D755 0%, #008578 100%)',
                borderBottom: '1px solid #e0e0e0',
                color: '#fff',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ pb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #D2D755 0%, #008578 100%)',
                      color: '#fff',
                      p: 1.5,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    Step
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #D2D755 0%, #008578 100%)',
                      color: '#fff',
                      p: 1.5,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    Resources
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>1.</strong> Review the ELF Policies and Chapter
                      Procedure for Sponsoring an Applicant.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item container xs={12} md={6} spacing={1}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/4dbccdd954ac441793c9bfaf65cd3f3d/latest"
                      label="ELF Policies Governing Loans"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/9e0ba857381d4607b523b2dee565a7b8/latest"
                      label="Chapter Procedure for Sponsoring an Applicant"
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>2.</strong> Review the Summary of Interview and
                      Recommendation of an ELF Applicant document, then
                      interview your candidate.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ButtonLink
                    href="https://peo-portal.com/library/document/e6de3ac529394156b5b0e1d394136946/latest"
                    label="Summary of Interview and Recommendation of an Applicant"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>3.</strong> After a successful chapter vote,
                      including the recommended loan amount, complete the online
                      ELF Recommendation Form.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6} />

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>4.</strong> Approximately two weeks after your
                      submission, please confirm your applicant has received her
                      application link. Contact elf@peodsm.org if she has not
                      received the link.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6} />

                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      maxWidth: 600,
                      mx: 'auto',
                      mt: 2,
                      textAlign: 'center',
                    }}
                  >
                    Both the chapter and student will be notified of the
                    decision. If the decision is favorable, due to privacy
                    concerns, the chapter will not be notified of the amount of
                    the approved loan.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions sx={{ pb: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} textAlign="center">
                  <Link
                    href="https://peointernational.communityforce.com/Funds/FundDetails.aspx?496F49596D593768464E3537506B6F754D5442315A38312B6676597151394F536376726B7A70417256394477734277704B49706D5252624A57594D375A615A777648646D7043424375436B3D"
                    target="_blank"
                    rel="noopener"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #D2D755 0%, #008578 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Start New ELF Recommendation
                    </Button>
                  </Link>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}
                  >
                    Click the button above to be directed to the CommunityForce
                    application system. CommunityForce is a third party vendor
                    we utilize for managing applications. Separate login
                    credentials will be required.
                    <br />
                    <Link
                      href="https://peo-portal.com/library/77fef27667fc4fb391e52322b710e0ff/view/f4728ea037294bdfae957f6d89b027d0"
                      target="_blank"
                      sx={{ ml: 1 }}
                      rel="noopener"
                    >
                      Click here for instructions for starting a new online
                      Recommendation Form
                    </Link>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} textAlign="center">
                  <Link
                    href="https://peointernational.communityforce.com"
                    target="_blank"
                    rel="noopener"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #D2D755 0%, #008578 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Continue Existing ELF Recommendation
                    </Button>
                  </Link>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}
                  >
                    Click the button above to be directed to the CommunityForce
                    application system. CommunityForce is a third party vendor
                    we utilize for managing applications. Separate login
                    credentials will be required.
                    <br />
                    <Link
                      sx={{ ml: 1 }}
                      href="https://peo-portal.com/library/f4b8177901e94f51ad99f4c618d74445/view/59511f483c444a35a6fd168f7768a560"
                      target="_blank"
                      rel="noopener"
                    >
                      Click here for instructions for continuing an existing
                      online Recommendation Form.
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </CardActions>
          </Card>
          {/* Eligibility Requirements */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Eligibility Requirements"
              subheader="A woman is eligible for consideration for an ELF loan if she:"
              sx={{
                background: 'linear-gradient(135deg, #D2D755 0%, #008578 100%)',
                color: '#fff',
                borderBottom: '1px solid #e0e0e0',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Is recommended by a local chapter of the P.E.O. Sisterhood
                      with a favorable vote on the amount of the loan
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Is a citizen or legal permanent resident of and resides in
                      the United States or Canada
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Submits an application within six to 24 months prior to
                      the stated graduation or course completion date
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Has been accepted by or is enrolled full- or part-time in
                      an accredited U.S. or Canadian post-secondary institution
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Has a high school diploma or the equivalent
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Demonstrates the academic ability to remain "in good
                      standing"
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Meets the minimum credit score of 625
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Does not exceed the indebtedness limit for the degree
                      level
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Has a qualified cosigner for the loan
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Does not have an outstanding ELF loan
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#008578',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Demonstrates financial need
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Maximum Loan Amounts and Application Process */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  boxShadow: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <CardHeader
                  title="Maximum Loan Amounts"
                  sx={{
                    background:
                      'linear-gradient(135deg, #D2D755 0%, #008578 100%)',
                    color: '#fff',
                    borderBottom: '1px solid #e0e0e0',
                  }}
                  titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#008578',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          $12,000 for an accredited non-degree or associate
                          degree program
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#008578',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          $15,000 for bachelor's degree programs
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#008578',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          $20,000 for master's degree programs
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#008578',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          $25,000 for doctoral degree programs
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  boxShadow: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <CardHeader
                  title="Application Process"
                  sx={{
                    background:
                      'linear-gradient(135deg, #D2D755 0%, #008578 100%)',
                    color: '#fff',
                    borderBottom: '1px solid #e0e0e0',
                  }}
                  titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#008578',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          Members of a local P.E.O. chapter will interview a
                          student before deciding to recommend her.
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#008578',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          The chapter will vote to recommend a student and
                          complete a recommendation form.
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#008578',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          P.E.O. Executive Office will send her a link to an
                          online application, which she has 30 days to complete.
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#008578',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          The Educational Loan Fund trustees then review the
                          application. The average processing time is eight to
                          12 weeks, provided all requirements are promptly met.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card
                sx={{
                  boxShadow: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <CardHeader
                  title="Become a Legacy Chapter"
                  sx={{
                    background:
                      'linear-gradient(135deg, #D2D755 0%, #008578 100%)',
                    color: '#fff',
                    borderBottom: '1px solid #e0e0e0',
                  }}
                  titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography
                        variant="body1"
                        sx={{ lineHeight: 1.6 }}
                        padding={2}
                      >
                        The P.E.O. Educational Loan Fund Legacy Chapter Program
                        offers the opportunity for local chapters to build their
                        own chapter legacy by supporting future generations of
                        women learners. By making a lump-sum contribution of
                        $500 or more, a chapter can proudly bear the title of an
                        "ELF Legacy Chapter." This recognition not only
                        showcases your dedication, but also aligns your chapter
                        with the legacy of ELF.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="body1"
                        sx={{ lineHeight: 1.6 }}
                        padding={2}
                      >
                        To become a Legacy Chapter, make sure your chapter's
                        contribution reaches your state, provincial or district
                        (s/p/d) treasurer or executive assistant by January 31.
                        (Chapters in Hawaii should send their contributions to
                        the International Chapter Treasurer).
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="body1"
                        sx={{ lineHeight: 1.6 }}
                        padding={2}
                      >
                        Individuals can also play a pivotal role in this legacy
                        journey. Lump-sum donations of $500 or more qualify your
                        chapter as a Legacy Chapter. Specify your chapter's name
                        on the Individual Donation Form to create this legacy
                        for your chapter. To be considered a charitable
                        donation, this contribution must be made payable to
                        P.E.O. Educational Loan Fund and be sent to the P.E.O.
                        Executive Office by January 31.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="body1"
                        sx={{ lineHeight: 1.6 }}
                        padding={2}
                      >
                        S/P/D chapter gift transmittals that include donations
                        qualifying for the Legacy Chapter program must be
                        received at the P.E.O. Executive Office in Des Moines no
                        later than February 28/29 to be recognized at the
                        upcoming s/p/d convention. Gifts received by the
                        executive office after February 28/29 will be processed
                        and recognized at the following year’s convention.
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>
    </Container>
  );
};

export default ELFLanding;
